<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useAuth } from '@/composables/useAuth'
import { useCurrencyFormat } from '@/composables/useCurrencyFormat'
import { useCompanyStore } from '@/stores/company'
import { planNameToPublicCode } from '@/composables/useWidgetGrid'
import { Input } from '@/components/ui/input'
import { useCouponCodeInput } from '@/modules/plans/composables/useCompanySubscriptions'
import useSubscription from '@/modules/plans/composables/useSubscription'
import useCompanyCredit from '@/modules/companies/composables/useCompanyCredit'
import useCompanyOpenPayCard from '@/modules/companies/composables/useCompanyOpenPayCard'
import SubscriptionSuccessDialog from '@/modules/plans/components/SubscriptionSuccessDialog.vue'
import SubscriptionLoadingModal from '@/modules/plans/components/SubscriptionLoadingModal.vue'
import type {
	Subscription,
	ScheduledSwapResponse,
	SubscriptionActionResponse,
	SubscriptionPaymentMethod,
} from '@/modules/plans/interfaces/subscription'

type Phase = 'compare' | 'checkout'

interface Props {
	open: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

const { user } = useAuth()
const { company } = storeToRefs(useCompanyStore())
const toast = useToast()

const companyId = computed(() =>
	user.value?.companyId ? Number(user.value.companyId) : undefined,
)

// ── Plan comparison table ────────────────────────────────────────────────────

const PLAN_FEATURES = [
	{ name: 'Dashboard',              start: 'Básico',         pro: 'Corporativo',        business: 'Ejecutivo avanzado' },
	{ name: 'Reportes pre-armados',   start: '3 estándar',     pro: 'Hasta 10 estándar',  business: 'Ilimitados + custom' },
	{ name: 'Exportación a Excel',    start: 'Sí',             pro: 'Sí',                 business: 'Sí' },
	{ name: 'Dashboards config.',     start: 'No',             pro: 'Intermedio',         business: 'Sí' },
	{ name: 'Reportería de ahorros',  start: 'No',             pro: 'Básica',             business: 'Avanzada' },
] as const

const PLAN_COLS = [
	{ code: 'start',    label: 'Start',     badge: false },
	{ code: 'pro',      label: 'Pro',       badge: true  },
	{ code: 'business', label: 'Business+', badge: false },
] as const

// ── Subscription ─────────────────────────────────────────────────────────────

const {
	data: subscriptionData,
	isLoading: subscriptionLoading,
	createSubscription,
	createLoading,
	swapSubscription,
	swapLoading,
	useCouponPricing,
} = useSubscription({ companyId })

const current    = computed(() => subscriptionData.value?.subscription ?? null)
const plans      = computed(() => subscriptionData.value?.plans ?? [])
const planByCode = computed(() => Object.fromEntries(plans.value.map((p) => [p.code, p])))
const selectedPlanMeta = computed(() => planByCode.value[selectedCode.value])

// ── Payment helpers ───────────────────────────────────────────────────────────

const { credit: companyCredit, isLoading: creditLoading } = useCompanyCredit({
	params: { companyId },
	immediate: true,
})

const { cards: companyCards } = useCompanyOpenPayCard({
	params: { companyId },
	immediate: true,
})

// ── OpenPay ───────────────────────────────────────────────────────────────────

const openPay      = (window as any).OpenPay
const deviceDataId = ref('')

onMounted(() => {
	if (!openPay) return
	openPay.setId(import.meta.env.VITE_OPENPAY_ID)
	openPay.setApiKey(import.meta.env.VITE_OPENPAY_KEY)
	openPay.setSandboxMode(import.meta.env.VITE_OPENPAY_PRODUCTION_MODE !== 'true')
	deviceDataId.value = openPay.deviceData.setup('planGateForm')
})

// ── Local state ───────────────────────────────────────────────────────────────

const phase            = ref<Phase>('compare')
const selectedCode     = ref('')
const paymentMethod    = ref<SubscriptionPaymentMethod>('credit')
const selectedCardId   = ref('')
const successSub       = ref<Subscription | null>(null)
const scheduledSwap    = ref<ScheduledSwapResponse | null>(null)
const showSuccess      = ref(false)
const isProcessing     = computed(() => createLoading.value || swapLoading.value)

const { couponCode, couponCodeCommitted, onCouponInput, resetCoupon } = useCouponCodeInput()

const {
	isSwap,
	swapPreview,
	couponPreview,
	couponHasError,
	couponErrorText,
	couponIsValidating,
	couponDiscountAmount,
} = useCouponPricing(selectedCode, couponCodeCommitted)

// Lo que se cobra hoy: en swap es el prorrateo (nunca "/ mes", es un cargo de
// una vez), en alta el precio de lista del plan (recurrente).
const grossAmount = computed(() =>
	isSwap.value ? swapPreview.value?.proratedAmount ?? 0 : selectedPlanMeta.value?.priceMxn ?? 0
)
const netAmount = computed(() => Math.max(0, grossAmount.value - couponDiscountAmount.value))

const inputErrorClass = 'border-destructive focus-visible:ring-destructive/30'

watch(() => props.open, (v) => {
	if (!v) {
		phase.value         = 'compare'
		selectedCode.value  = ''
		resetCoupon()
		successSub.value    = null
		scheduledSwap.value = null
		showSuccess.value   = false
	}
})

// ── Current plan rank ─────────────────────────────────────────────────────────

const RANK: Record<string, number> = { free: 0, start: 1, pro: 2, business: 3 }

// planNameToPublicCode y no planNameToCode: esta modal compara rangos DENTRO del
// catálogo público, y un plan asignado a mano no tiene lugar en ese orden. Cae a
// 'free' para que la comparativa siga pintando, pero la empresa que trae uno de
// esos planes no ve esta modal — no está bloqueada por features.
const currentPlanCode = computed(() =>
	planNameToPublicCode(company.value?.activePlan)
)

const cellStyle = (code: string) => {
	const isCurrent = code === currentPlanCode.value
	const isUpgrade = RANK[code] > RANK[currentPlanCode.value]
	return isCurrent ? 'bg-blue-50/60 text-blue-800 font-semibold'
	     : isUpgrade ? 'text-gray-800'
	     :             'text-gray-400'
}

// ── Actions ───────────────────────────────────────────────────────────────────

function selectPlan(code: string) {
	selectedCode.value = code
	const cardList = (companyCards.value ?? []) as { id: string }[]
	if (cardList.length) selectedCardId.value = cardList[0].id
	phase.value = 'checkout'
}

const creditAvailable = computed(() => (companyCredit.value as any)?.available ?? 0)
const hasEnoughCredit = computed(() => {
	if (!selectedPlanMeta.value) return false
	return creditAvailable.value >= netAmount.value
})

// La empresa puede no tener crédito configurado (no solo saldo en 0): en ese
// caso ocultamos la opción igual que con las tarjetas, y si "crédito" seguía
// seleccionado por default lo mandamos a "tarjeta" en cuanto sabemos que no aplica.
watch([companyCredit, creditLoading], () => {
	if (!creditLoading.value && !companyCredit.value && paymentMethod.value === 'credit') {
		paymentMethod.value = 'card'
	}
})

async function confirmUpgrade() {
	if (!selectedCode.value) return

	const isCard          = paymentMethod.value === 'card'
	const cardId          = isCard ? Number(selectedCardId.value) : undefined
	const deviceSessionId = isCard ? deviceDataId.value : undefined
	const normalizedCoupon = couponCode.value.trim().toUpperCase() || undefined

	try {
		if (current.value) {
			const res = await swapSubscription(current.value.id, {
				planCode: selectedCode.value,
				paymentMethod: paymentMethod.value,
				cardId,
				deviceSessionId,
				couponCode: normalizedCoupon,
			})
			if ('swapType' in res) scheduledSwap.value = res as ScheduledSwapResponse
			else successSub.value = (res as SubscriptionActionResponse).subscription
		} else {
			const res = await createSubscription({
				planCode: selectedCode.value,
				paymentMethod: paymentMethod.value,
				cardId,
				couponCode: normalizedCoupon,
			})
			successSub.value = res.subscription
		}
		showSuccess.value = true
	} catch (err) {
		const message =
			(err as { response?: { data?: { message?: string } } })?.response?.data
				?.message ?? 'No se pudo procesar la operación'
		toast.error(message)
	}
}

function onSuccessAccept() {
	showSuccess.value = false
	emit('update:open', false)
}

function tryClose() {
	if (phase.value === 'compare') emit('update:open', false)
}

function priceLabel(code: string) {
	const plan = planByCode.value[code]
	if (!plan) return '...'
	if (!plan.priceMxn) return 'Gratis'
	return `${useCurrencyFormat(plan.priceMxn, 'MXN')} / mes`
}
</script>

<template>
	<Teleport to="body">
		<Transition
			enter-active-class="transition-all duration-200"
			enter-from-class="opacity-0"
			leave-active-class="transition-all duration-150"
			leave-to-class="opacity-0"
		>
			<div
				v-if="open"
				class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
				@click.self="tryClose"
			>
				<Transition
					enter-active-class="transition-all duration-200"
					enter-from-class="opacity-0 scale-95"
					leave-active-class="transition-all duration-150"
					leave-to-class="opacity-0 scale-95"
					mode="out-in"
				>
					<!-- ── COMPARE PHASE ── -->
					<div
						v-if="phase === 'compare'"
						key="compare"
						class="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
					>
						<!-- Close button -->
						<button
							type="button"
							class="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
							@click="emit('update:open', false)"
						>
							<span class="material-symbols-outlined text-[20px]">close</span>
						</button>

						<!-- 426 cat + header -->
						<div class="flex flex-col items-center gap-3 px-6 pt-8 pb-4 text-center">
							<img
								src="https://http.cat/status/426"
								alt="426 Upgrade Required"
								class="h-32 w-auto rounded-xl object-cover shadow-sm"
							/>
							<div>
								<p class="text-xs font-semibold uppercase tracking-widest text-amber-500">
									426 Upgrade Required
								</p>
								<h2 class="mt-1 text-xl font-bold text-gray-900">
									Actualiza tu plan para acceder
								</h2>
								<p class="mt-1 text-sm text-gray-500">
									Esta función requiere un plan superior.
									Elige el que mejor se adapte a tu empresa.
								</p>
							</div>
						</div>

						<!-- Plan comparison table -->
						<div class="overflow-x-auto px-6">
							<table class="w-full border-collapse text-sm">
								<thead>
									<tr class="border-b border-gray-100">
										<th class="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
											Módulo
										</th>
										<th
											v-for="col in PLAN_COLS"
											:key="col.code"
											:class="[
												'px-3 py-3 text-center align-top',
												col.code === currentPlanCode ? 'bg-blue-50/40' : '',
											]"
										>
											<div class="flex flex-col items-center gap-1">
												<span class="flex items-center gap-1 text-sm font-bold text-gray-900">
													{{ col.label }}
													<span v-if="col.badge" class="text-amber-400">★</span>
												</span>
												<span class="text-xs text-gray-400">
													{{ subscriptionLoading ? '…' : priceLabel(col.code) }}
												</span>
												<span
													v-if="col.code === currentPlanCode"
													class="mt-0.5 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
												>
													Tu plan
												</span>
											</div>
										</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="row in PLAN_FEATURES"
										:key="row.name"
										class="border-b border-gray-50"
									>
										<td class="py-2.5 pr-4 text-sm font-medium text-gray-700">
											{{ row.name }}
										</td>
										<td
											v-for="col in PLAN_COLS"
											:key="col.code"
											:class="[
												'px-3 py-2.5 text-center text-sm',
												col.code === currentPlanCode ? 'bg-blue-50/30' : '',
												cellStyle(col.code),
											]"
										>
											<span
												v-if="row[col.code as keyof typeof row] === 'Sí'"
												class="material-symbols-outlined text-[16px] text-emerald-500"
											>
												check
											</span>
											<span
												v-else-if="row[col.code as keyof typeof row] === 'No'"
												class="material-symbols-outlined text-[16px] text-gray-300"
											>
												remove
											</span>
											<span v-else>{{ row[col.code as keyof typeof row] }}</span>
										</td>
									</tr>
									<!-- CTA row -->
									<tr>
										<td class="py-3 pr-4" />
										<td
											v-for="col in PLAN_COLS"
											:key="col.code"
											:class="[
												'px-3 py-3 text-center',
												col.code === currentPlanCode ? 'bg-blue-50/30' : '',
											]"
										>
											<span
												v-if="col.code === currentPlanCode"
												class="text-xs font-semibold text-blue-600"
											>
												Plan actual
											</span>
											<button
												v-else-if="RANK[col.code] > RANK[currentPlanCode]"
												type="button"
												class="w-full rounded-lg bg-[#253C70] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#1e3260]"
												@click="selectPlan(col.code)"
											>
												Actualizar a {{ col.label }}
											</button>
											<span v-else class="text-xs text-gray-300">—</span>
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						<div class="px-6 pb-6 pt-2 text-center text-xs text-gray-400">
							Al actualizar tu plan aceptas los términos de servicio.
						</div>
					</div>

					<!-- ── CHECKOUT PHASE ── -->
					<div
						v-else-if="phase === 'checkout'"
						key="checkout"
						class="w-full max-w-md rounded-2xl bg-white shadow-2xl"
					>
						<!-- Header (no close button) -->
						<div class="flex items-center gap-3 border-b border-gray-100 px-6 py-4">
							<button
								type="button"
								class="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 transition-colors"
								:disabled="isProcessing"
								@click="phase = 'compare'"
							>
								<span class="material-symbols-outlined text-[20px]">arrow_back</span>
							</button>
							<div>
								<h2 class="text-base font-bold text-gray-900">Confirmar actualización</h2>
								<p class="text-xs text-gray-400">Completa el pago para activar</p>
							</div>
						</div>

						<div class="px-6 py-5 space-y-5">
							<!-- Selected plan summary -->
							<div class="rounded-xl border border-[#253C70]/20 bg-[#253C70]/5 p-4">
								<p class="text-xs font-semibold uppercase tracking-wide text-[#253C70]">
									Plan seleccionado
								</p>
								<p class="mt-1 text-xl font-bold text-gray-900">
									{{ selectedPlanMeta?.name ?? selectedCode }}
								</p>
								<div v-if="selectedPlanMeta" class="text-sm text-gray-500">
									<template v-if="couponDiscountAmount > 0">
										<span class="line-through text-gray-400">{{ useCurrencyFormat(grossAmount, 'MXN') }}</span>
										<span class="ml-2 font-semibold text-emerald-700">
											{{ useCurrencyFormat(netAmount, 'MXN') }}<template v-if="!isSwap"> / mes</template>
										</span>
									</template>
									<template v-else-if="isSwap">
										{{ useCurrencyFormat(grossAmount, 'MXN') }}
										<span class="text-xs text-gray-400">(cargo de hoy)</span>
									</template>
									<template v-else>{{ priceLabel(selectedCode) }}</template>
								</div>
							</div>

							<!-- Payment method -->
							<div class="space-y-2">
								<p class="text-sm font-semibold text-gray-700">Método de pago</p>

								<!-- Credit: solo si la empresa tiene crédito configurado, igual que tarjetas -->
								<label
									v-if="companyCredit"
									:class="[
										'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors',
										paymentMethod === 'credit' ? 'border-[#253C70] bg-[#253C70]/5' : 'border-gray-200 hover:border-gray-300',
									]"
								>
									<input
										v-model="paymentMethod"
										type="radio"
										value="credit"
										class="mt-0.5 accent-[#253C70]"
									/>
									<div class="flex-1">
										<div class="flex items-center gap-2">
											<span class="material-symbols-outlined text-[16px] text-violet-600">corporate_fare</span>
											<span class="text-sm font-medium text-gray-800">Crédito empresarial</span>
										</div>
										<p
											:class="[
												'text-xs mt-0.5',
												paymentMethod === 'credit' && !hasEnoughCredit ? 'text-red-500 font-medium' : 'text-gray-400',
											]"
										>
											Disponible: {{ useCurrencyFormat(creditAvailable, 'MXN') }}
											<span v-if="paymentMethod === 'credit' && !hasEnoughCredit"> — saldo insuficiente</span>
										</p>
									</div>
								</label>

								<!-- Card -->
								<label
									v-if="(companyCards as any[])?.length"
									:class="[
										'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors',
										paymentMethod === 'card' ? 'border-[#253C70] bg-[#253C70]/5' : 'border-gray-200 hover:border-gray-300',
									]"
								>
									<input
										v-model="paymentMethod"
										type="radio"
										value="card"
										class="mt-0.5 accent-[#253C70]"
									/>
									<div class="flex-1">
										<div class="flex items-center gap-2">
											<span class="material-symbols-outlined text-[16px] text-sky-600">credit_card</span>
											<span class="text-sm font-medium text-gray-800">Tarjeta guardada</span>
										</div>
										<select
											v-if="paymentMethod === 'card'"
											v-model="selectedCardId"
											class="mt-2 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#253C70]/30"
										>
											<option
												v-for="card in (companyCards as any[])"
												:key="card.id"
												:value="card.id"
											>
												{{ card.brand ?? '' }} **** {{ card.cardNumber?.slice(-4) ?? '····' }}
												<span v-if="card.holderName"> — {{ card.holderName }}</span>
											</option>
										</select>
									</div>
								</label>
							</div>

							<!-- Coupon code -->
							<div>
								<label for="plan-gate-coupon" class="block text-sm font-semibold text-gray-700 mb-2">Código de cupón</label>
								<Input
									id="plan-gate-coupon"
									v-model="couponCode"
									type="text"
									placeholder="Opcional"
									class="uppercase"
									:class="couponHasError ? inputErrorClass : ''"
									@input="onCouponInput"
								/>
								<div role="status" aria-live="polite">
									<p v-if="couponIsValidating" class="mt-1.5 flex items-center gap-1.5 text-xs text-gray-400">
										<span class="inline-block w-3 h-3 border-2 border-gray-200 border-t-[#253C70] rounded-full animate-spin" />
										Validando cupón…
									</p>
									<p v-else-if="couponHasError" class="mt-1.5 flex items-center gap-1 text-xs text-destructive">
										<span class="material-symbols-outlined text-[14px]">error</span>
										{{ couponErrorText }}
									</p>
									<p v-else-if="couponDiscountAmount > 0" class="mt-1.5 flex items-center gap-1 text-xs text-emerald-600">
										<span class="material-symbols-outlined text-[14px]">check_circle</span>
										Cupón aplicado: -{{ useCurrencyFormat(couponDiscountAmount, 'MXN') }}
									</p>
								</div>
							</div>

							<!-- Confirm button -->
							<button
								type="button"
								:disabled="isProcessing || couponIsValidating || couponHasError || (paymentMethod === 'credit' && !hasEnoughCredit) || (paymentMethod === 'card' && !selectedCardId)"
								class="w-full rounded-xl bg-[#253C70] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1e3260] disabled:opacity-40 disabled:cursor-not-allowed"
								@click="confirmUpgrade"
							>
								Confirmar pago y activar plan
							</button>

							<p class="text-center text-xs text-gray-400">
								No puedes cerrar esta ventana hasta completar el pago.
							</p>
						</div>
					</div>
				</Transition>
			</div>
		</Transition>
	</Teleport>

	<!-- Loading overlay -->
	<SubscriptionLoadingModal
		:creating="createLoading"
		:swapping="swapLoading"
	/>

	<!-- Success dialog -->
	<SubscriptionSuccessDialog
		:open="showSuccess"
		:success-subscription="successSub"
		:scheduled-swap="scheduledSwap"
		@update:open="showSuccess = $event"
		@accept="onSuccessAccept"
	/>
</template>
